<template>
  <b-modal
    :id="modalId"
    v-model="showModal"
    title="Liên kết với cửa hàng"
    @ok="handleOk"
    @cancel="handleCancel"
    @hidden="handleHidden"
    ok-title="Lưu"
    cancel-title="Hủy"
    :ok-disabled="!selectedStoreId || isSaving"
    size="md"
  >
    <div class="store-association-modal">
      <div class="mb-3">
        <p class="mb-2">
          Liên kết gian hàng <strong>{{ shopName }}</strong> với cửa hàng
          ClickBuy:
        </p>
      </div>

      <b-form-group
        label="Chọn cửa hàng"
        label-for="store-select"
        :description="
          stores.length === 1 && isAutoSelected
            ? 'Tự động chọn cửa hàng duy nhất'
            : 'Chọn cửa hàng để liên kết với gian hàng này'
        "
      >
        <b-form-select
          id="store-select"
          class="select-style"
          v-model="selectedStoreId"
          :options="storeOptions"
          :disabled="isAutoSelected && !allowChange"
          size="sm"
        >
          <template #first>
            <b-form-select-option :value="null" disabled>
              -- Chọn cửa hàng --
            </b-form-select-option>
          </template>
        </b-form-select>

        <div v-if="isAutoSelected && !allowChange" class="mt-2">
          <b-button variant="link" size="sm" @click="changeStore" class="p-0">
            <i class="flaticon2-edit"></i> Thay đổi cửa hàng
          </b-button>
        </div>
      </b-form-group>

      <b-alert v-if="error" show variant="danger" class="mt-3">
        {{ error }}
      </b-alert>

      <b-alert v-if="stores.length === 0" show variant="warning" class="mt-3">
        <h6 class="alert-heading">Không có cửa hàng</h6>
        <p class="mb-0">
          Bạn chưa có cửa hàng nào trong hệ thống. Vui lòng tạo cửa hàng trước
          khi liên kết.
        </p>
      </b-alert>

      <div v-if="isSaving" class="text-center mt-3">
        <b-spinner variant="primary" small class="mr-2"></b-spinner>
        <span class="text-muted">Đang lưu liên kết...</span>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { getToastInstance } from '@/utils/toastHelper';
import { RepositoryFactory } from '@/repositories/RepositoryFactory';

const platformRepository = RepositoryFactory.get('platformIntegration');

export default {
  name: 'StoreAssociationModal',
  props: {
    shopName: {
      type: String,
      required: true,
    },
    configId: {
      type: [String, Number],
      required: true,
    },
    stores: {
      type: Array,
      default: () => [],
    },
    value: {
      type: Boolean,
      default: false,
    },
    modalId: {
      type: String,
      default: 'store-association-modal',
    },
  },
  data() {
    return {
      selectedStoreId: null,
      allowChange: false,
      isSaving: false,
      error: null,
    };
  },
  computed: {
    showModal: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    storeOptions() {
      return this.stores.map((store) => ({
        value: store.id,
        text: store.name || store.shortName,
      }));
    },
    isAutoSelected() {
      return this.stores.length === 1 && this.selectedStoreId !== null;
    },
  },
  watch: {
    stores: {
      immediate: true,
      handler(newStores) {
        if (newStores.length === 1) {
          this.selectedStoreId = newStores[0].id;
          this.allowChange = false;
        }
      },
    },
    value(newValue) {
      if (newValue) {
        this.resetState();
      }
    },
  },
  methods: {
    ...getToastInstance(),

    resetState() {
      this.error = null;
      this.isSaving = false;
      this.allowChange = false;
      if (this.stores.length === 1) {
        this.selectedStoreId = this.stores[0].id;
      } else {
        this.selectedStoreId = null;
      }
    },

    changeStore() {
      this.allowChange = true;
    },

    async handleOk(bvModalEvent) {
      bvModalEvent.preventDefault();
      await this.saveAssociation();
    },

    handleCancel() {
      this.$emit('cancel');
      this.showModal = false;
    },

    handleHidden() {
      this.resetState();
    },

    async saveAssociation() {
      if (!this.selectedStoreId) {
        this.error = 'Vui lòng chọn cửa hàng';
        return;
      }

      try {
        this.isSaving = true;
        this.error = null;

        const data = {
          configId: this.configId,
          storeId: this.selectedStoreId,
        };

        await platformRepository.saveConfig(data);

        this.$emit('saved', {
          storeId: this.selectedStoreId,
          configId: this.configId,
        });
        this.showModal = false;
      } catch (error) {
        console.error('Error saving store association:', error);

        if (error.response?.status === 409) {
          this.error = 'Gian hàng này đã được liên kết với cửa hàng khác.';
        } else if (error.response?.data?.message) {
          this.error = error.response.data.message;
        } else {
          this.error = 'Không thể lưu liên kết. Vui lòng thử lại.';
        }

        this.makeToastFailure(this.error);
      } finally {
        this.isSaving = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.store-association-modal {
  strong {
    color: #3699ff;
  }

  :deep(.select-style.form-control:focus),
  :deep(.select-style.form-control:focus-visible) {
    border-color: #ced4da;
    box-shadow: none;
    outline: 0;
  }

  :deep(.form-control) {
    &:disabled {
      background-color: #f3f6f9;
      cursor: not-allowed;
    }
  }
}
</style>
